import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Input } from '@schibsted-svp/react-ui';

import { getProvider } from '../services/svpProvider';
import { ContainerHeader } from '../components/Container/ContainerHeader/ContainerHeader';
import { FormField } from '../components/Form/shared/FormField/FormField';
import { AssetsList } from '../components/AssetsList/AssetsList';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchAssets } from '../hooks/useSearchAssets';
import { CenteredSpinner } from '../components/CenteredSpinner/CenteredSpinner';
import { Spacer } from '../components/Spacer/Spacer';

import type { SvpAsset } from '../types';
import type { SvpSearchAssetsParams } from '../services/svp/shared/requestSVP';
import type { ChangeEvent, FC } from 'react';

const filters: Record<
  string,
  Record<
    string,
    {
      label: string;
      useVerticalThumbnails?: boolean;
      searchParams?: SvpSearchAssetsParams;
    }
  >
> = {
  vgtv: {
    documentaries: {
      label: 'Documentaries',
      useVerticalThumbnails: true,
      searchParams: {
        filter: {
          categoryId: '121',
          streamType: 'vod',
          duration: '>500000',
        },
        sort: 'created|published',
      },
    },
    series: {
      label: 'Series',
      searchParams: {
        filter: {
          'category.isSeries': 'true',
          streamType: 'vod',
          duration: '>500000',
        },
        sort: 'created',
      },
    },
  },
};

const buttons = Object.entries(filters[getProvider()] ?? {}).map(
  ([key, { label }]) => ({ key, label })
);

if (buttons.length) {
  buttons.unshift({ key: '', label: 'All' });
}

export const Assets: FC = () => {
  const { kind = '' } = useParams<{ kind: string }>();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);

  const options = useMemo(() => {
    const provider = getProvider();
    return filters[provider]?.[kind] || {};
  }, [kind]);

  const searchParams = useMemo((): SvpSearchAssetsParams => {
    const params: SvpSearchAssetsParams = {
      ...options.searchParams,
    };

    if (debouncedQuery) {
      params.query = debouncedQuery;
    } else {
      params.limit = 30;
      params.sort = 'published|created';
    }

    return params;
  }, [debouncedQuery, options]);

  const { data: assets = [], isLoading } = useSearchAssets(searchParams);

  const getAssetLink = useCallback((asset: SvpAsset) => {
    return `/assets/${asset.id}/edit`;
  }, []);

  return (
    <>
      <ContainerHeader>
        <FormField label="Search by title or ID">
          <Input
            value={query}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              setQuery(value);
            }}
          />
        </FormField>
        <Spacer />
        {buttons.map(({ key, label }) => (
          <Link to={`/assets${key ? `/${key}` : ''}`} key={key}>
            <Button
              type="button"
              variant={key === kind ? undefined : 'standard'}
            >
              {label}
            </Button>
          </Link>
        ))}
      </ContainerHeader>
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <AssetsList
          useVerticalThumbnails={options.useVerticalThumbnails}
          assets={assets}
          getLink={getAssetLink}
        />
      )}
    </>
  );
};
